'use client';
import React, { useState } from 'react';
import { Calendar, Users, CreditCard, Lock, CheckCircle, X } from 'lucide-react';
import { useForm } from 'react-hook-form';

type PaymentForm = {
  cardName: string;
  cardNumber: string;
  expiry: string;
  cvc: string;
  email: string;
};

const PRICE_PER_NIGHT = 145;
const CLEANING_FEE = 55;
const SERVICE_FEE_RATE = 0.12;

export default function BookingSidebar() {
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<PaymentForm>();

  const nights = (() => {
    if (!checkIn || !checkOut) return 0;
    const diff = new Date(checkOut).getTime() - new Date(checkIn).getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  })();

  const subtotal = nights * PRICE_PER_NIGHT;
  const serviceFee = Math.round(subtotal * SERVICE_FEE_RATE);
  const total = subtotal + CLEANING_FEE + serviceFee;

  // BACKEND INTEGRATION: POST /api/bookings/create + POST /api/payments/stripe
  const onPaymentSubmit = async (_data: PaymentForm) => {
    setPaymentLoading(true);
    await new Promise((r) => setTimeout(r, 2000));
    setPaymentLoading(false);
    setPaymentSuccess(true);
  };

  if (paymentSuccess) {
    return (
      <div className="xl:sticky xl:top-24 card-base p-6 text-center animate-fade-in">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h3 className="font-700 text-lg text-foreground mb-2">Booking Confirmed!</h3>
        <p className="text-sm text-muted-foreground mb-1">Booking reference: <span className="font-700 text-foreground tabular-nums">VR-2026-3847</span></p>
        <p className="text-xs text-muted-foreground mb-4">A confirmation has been sent to your email. Maria will send check-in instructions 24h before arrival.</p>
        <div className="bg-green-50 rounded-xl p-4 text-left space-y-1.5 mb-4">
          <p className="text-xs text-green-700"><span className="font-600">Check-in:</span> {checkIn} at 15:00</p>
          <p className="text-xs text-green-700"><span className="font-600">Check-out:</span> {checkOut} at 11:00</p>
          <p className="text-xs text-green-700"><span className="font-600">Total charged:</span> €{total}</p>
        </div>
        <button onClick={() => { setPaymentSuccess(false); setShowPayment(false); }} className="btn-outline w-full justify-center text-sm py-2.5">
          Make Another Booking
        </button>
      </div>
    );
  }

  return (
    <div className="xl:sticky xl:top-24 card-base p-6 shadow-card">
      {!showPayment ? (
        <>
          {/* Price header */}
          <div className="flex items-end justify-between mb-5">
            <div>
              <span className="text-2xl font-800 text-foreground tabular-nums">€{PRICE_PER_NIGHT}</span>
              <span className="text-muted-foreground text-sm">/night</span>
            </div>
            <div className="flex items-center gap-1 text-sm">
              <span className="text-accent">★</span>
              <span className="font-700">4.9</span>
              <span className="text-muted-foreground">(48)</span>
            </div>
          </div>

          {/* Date pickers */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <label className="block text-[10px] font-700 uppercase tracking-wider text-muted-foreground mb-1">Check-in</label>
              <div className="relative">
                <Calendar size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full pl-8 pr-2 py-2.5 bg-muted rounded-xl text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-700 uppercase tracking-wider text-muted-foreground mb-1">Check-out</label>
              <div className="relative">
                <Calendar size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="date"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  min={checkIn || new Date().toISOString().split('T')[0]}
                  className="w-full pl-8 pr-2 py-2.5 bg-muted rounded-xl text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          </div>

          {/* Guests */}
          <div className="mb-5">
            <label className="block text-[10px] font-700 uppercase tracking-wider text-muted-foreground mb-1">Guests</label>
            <div className="relative">
              <Users size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <select
                value={guests}
                onChange={(e) => setGuests(Number(e.target.value))}
                className="w-full pl-8 pr-3 py-2.5 bg-muted rounded-xl text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring appearance-none"
              >
                {[1, 2, 3, 4].map((n) => (
                  <option key={`guest-sel-${n}`} value={n}>{n} Guest{n > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Price breakdown */}
          {nights > 0 && (
            <div className="bg-muted/60 rounded-xl p-4 mb-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">€{PRICE_PER_NIGHT} × {nights} nights</span>
                <span className="font-600 tabular-nums">€{subtotal}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Cleaning fee</span>
                <span className="font-600 tabular-nums">€{CLEANING_FEE}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Service fee (12%)</span>
                <span className="font-600 tabular-nums">€{serviceFee}</span>
              </div>
              <div className="border-t border-border pt-2 flex justify-between text-sm font-700">
                <span>Total</span>
                <span className="tabular-nums">€{total}</span>
              </div>
            </div>
          )}

          {nights < 3 && nights > 0 && (
            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mb-4 flex items-center gap-1.5">
              ⚠️ Minimum stay is 3 nights
            </p>
          )}

          <button
            onClick={() => nights >= 3 && setShowPayment(true)}
            disabled={nights < 3}
            className="w-full btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {nights === 0 ? 'Select Dates to Book' : nights < 3 ? '3-Night Minimum Required' : `Reserve — €${total}`}
          </button>
          <p className="text-center text-xs text-muted-foreground mt-3 flex items-center justify-center gap-1">
            <Lock size={11} /> Secure payment via Stripe · You won&apos;t be charged yet
          </p>
        </>
      ) : (
        /* Payment Form */
        <form onSubmit={handleSubmit(onPaymentSubmit)}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-700 text-base text-foreground">Payment Details</h3>
            <button type="button" onClick={() => setShowPayment(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
              <X size={16} />
            </button>
          </div>

          {/* Order summary */}
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 mb-5">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Sunset Terrace Suite · {nights} nights</span>
              <span className="font-700 tabular-nums">€{total}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{checkIn} → {checkOut} · {guests} guest{guests > 1 ? 's' : ''}</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-700 uppercase tracking-wider text-muted-foreground mb-1.5">Email</label>
              <input
                {...register('email', { required: 'Email required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' } })}
                type="email"
                placeholder="your@email.com"
                className="input-base text-sm"
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-700 uppercase tracking-wider text-muted-foreground mb-1.5">Name on Card</label>
              <input
                {...register('cardName', { required: 'Name required' })}
                placeholder="Sophie Marchand"
                className="input-base text-sm"
              />
              {errors.cardName && <p className="mt-1 text-xs text-red-500">{errors.cardName.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-700 uppercase tracking-wider text-muted-foreground mb-1.5">Card Number</label>
              <div className="relative">
                <CreditCard size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  {...register('cardNumber', { required: 'Card number required', pattern: { value: /^\d{4}\s?\d{4}\s?\d{4}\s?\d{4}$/, message: 'Enter a valid 16-digit card number' } })}
                  placeholder="4242 4242 4242 4242"
                  className="input-base pl-10 text-sm tabular-nums"
                  maxLength={19}
                />
              </div>
              {errors.cardNumber && <p className="mt-1 text-xs text-red-500">{errors.cardNumber.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-700 uppercase tracking-wider text-muted-foreground mb-1.5">Expiry</label>
                <input
                  {...register('expiry', { required: 'Required', pattern: { value: /^\d{2}\/\d{2}$/, message: 'MM/YY' } })}
                  placeholder="MM/YY"
                  className="input-base text-sm tabular-nums"
                  maxLength={5}
                />
                {errors.expiry && <p className="mt-1 text-xs text-red-500">{errors.expiry.message}</p>}
              </div>
              <div>
                <label className="block text-xs font-700 uppercase tracking-wider text-muted-foreground mb-1.5">CVC</label>
                <input
                  {...register('cvc', { required: 'Required', pattern: { value: /^\d{3,4}$/, message: '3-4 digits' } })}
                  placeholder="123"
                  className="input-base text-sm tabular-nums"
                  maxLength={4}
                  type="password"
                />
                {errors.cvc && <p className="mt-1 text-xs text-red-500">{errors.cvc.message}</p>}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={paymentLoading}
            className="w-full btn-primary justify-center mt-5 disabled:opacity-60"
            style={{ minHeight: '48px' }}
          >
            {paymentLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Processing Payment...
              </span>
            ) : (
              <>
                <Lock size={15} />
                Pay €{total} Securely
              </>
            )}
          </button>
          <p className="text-center text-xs text-muted-foreground mt-2">
            Protected by Stripe · 256-bit SSL encryption
          </p>
        </form>
      )}
    </div>
  );
}